import React, { createContext, useContext, useCallback, useMemo } from "react";
import { ZodObject, ZodRawShape } from "zod";
import { useToast } from "@/hooks/useToast";
import { AxiosError } from "axios";
import { REQUEST_FAILED, UNEXPECTED_ERROR } from "@/utils/toastMessages";

interface ErrorHandlerContextType {
  handleApiError: <T extends ZodRawShape>(err: unknown, zodSchema?: ZodObject<T>) => void;
}

const ErrorHandlerContext = createContext<ErrorHandlerContextType | undefined>(undefined);

export const ErrorHandlerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showToast } = useToast();

  const handleApiError = useCallback(
    <T extends ZodRawShape>(err: unknown, zodSchema?: ZodObject<T>) => {
      if (err instanceof AxiosError) {
        if (err.response?.status === 400) {
          showToast({
            type: "error",
            message: err.response.data?.detail || REQUEST_FAILED,
          });
        } else if (err.response?.status === 429) {
          // Rate limit error - show user-friendly message and don't retry
          const detail = err.response.data?.detail || "Daily limit reached. Please try again tomorrow.";
          showToast({
            type: "error",
            message: detail,
          });
        } else if (err.response?.status === 422 && zodSchema) {
          const details = err.response.data?.detail;
          let errorMsg = "Some required fields are missing or invalid.";
          if (Array.isArray(details)) {
            const fieldLabels: Record<string, string> = {};
            for (const key in zodSchema.shape) {
              const typedKey = key as keyof typeof zodSchema.shape;
              fieldLabels[key] = zodSchema.shape[typedKey]._def.description || key;
            }
            errorMsg = details
              .map((item: { loc: string[]; msg: string }) => {
                const field = Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : "";
                const label = fieldLabels[field] || field;
                return `${label}: ${item.msg}`;
              })
              .join("\n");
          }
          showToast({ type: "error", message: String(errorMsg) });
        } else {
          showToast({ type: "error", message: UNEXPECTED_ERROR });
        }
      }
    },
    [showToast] // Only depends on showToast
  );

  // Memoize context value to prevent re-renders
  const contextValue = useMemo(() => ({ handleApiError }), [handleApiError]);

  return (
    <ErrorHandlerContext.Provider value={contextValue}>
      {children}
    </ErrorHandlerContext.Provider>
  );
};

export const useErrorHandler = () => {
  const ctx = useContext(ErrorHandlerContext);
  if (!ctx) throw new Error("useErrorHandler must be used within ErrorHandlerProvider");
  return ctx.handleApiError;
};
